// searchAbbrev.js — client-side collection search normalization
// Expands common house/fragrance abbreviations and folds accents so casual
// queries ("jpg", "ysl", "acqua de gio") match the catalog vocabulary.

const ABBREVS = {
  jpg: 'jean paul gaultier', ysl: 'yves saint laurent',
  adg: 'acqua di gio', tf: 'tom ford', pdm: 'parfums de marly',
  mfk: 'maison francis kurkdjian', cdg: 'comme des garcons',
  vr: 'viktor rolf', ch: 'carolina herrera', bdc: 'bleu de chanel',
  bir: 'born in roma', cdn: 'creed', swy: 'stronger with you',
}

const stripAccents = (s) =>
  String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')

// Symmetric normalizer — applied to BOTH the query and the haystack.
export function normalizeSearch(input) {
  let t = stripAccents(input).toLowerCase()
  // token-level abbreviation expansion
  t = t.split(/\s+/).map(tok => ABBREVS[tok] || tok).join(' ')
  // common spelling folds
  t = t.replace(/\bacqua\b/g, 'aqua').replace(/\bde\b/g, 'di')
  return t.replace(/\s+/g, ' ').trim()
}

// A typed fragment must match the START of a word, never the middle of one.
// Any non-alphanumeric counts as a boundary, so "azz" hits "Azzaro" and
// "L'Azzaro" but never "J-azz Club". A bare `includes()` ranked Maison
// Margiela Replica Jazz Club above the founder's own Azzaro bottles.
function containsAtWordStart(hay, word) {
  if (!word) return true
  for (let i = hay.indexOf(word); i !== -1; i = hay.indexOf(word, i + 1)) {
    if (i === 0 || !/[a-z0-9]/.test(hay[i - 1])) return true
  }
  return false
}

const identityHay = (frag) => normalizeSearch(
  [
    frag.name,
    frag.house,
    (frag.concentrations || []).join(' '),
    frag.concentration,
  ].filter(Boolean).join(' ')
)

const notesHay = (frag) => normalizeSearch(
  [
    (frag.top_notes || []).join(' '),
    (frag.mid_notes || []).join(' '),
    (frag.base_notes || []).join(' '),
    frag.scent_family,
  ].filter(Boolean).join(' ')
)

// Relevance tier, not a boolean: the shelf is fetched alphabetically by name, so
// without a score "wanted" ordered a rose-noted row above Azzaro Wanted purely
// because W sorts late. Higher is better; 0 means "no match" (filter it out).
export const SEARCH_SCORE = { NONE: 0, NOTES_ONLY: 1, IDENTITY: 2, LEADING: 3 }

export function fragranceSearchScore(frag, query) {
  const q = normalizeSearch(query)
  if (!q) return SEARCH_SCORE.LEADING
  const words = q.split(' ').filter(Boolean)
  if (words.length === 0) return SEARCH_SCORE.LEADING

  const identity = identityHay(frag)
  if (words.every(w => containsAtWordStart(identity, w))) {
    // House or product name literally begins with what was typed.
    return identity.startsWith(q) || containsAtWordStart(normalizeSearch(frag.house), q)
      ? SEARCH_SCORE.LEADING
      : SEARCH_SCORE.IDENTITY
  }

  // Fall back to the scent pyramid so note-led discovery still works, but it can
  // never outrank a row whose house/name actually matches.
  const combined = `${identity} ${notesHay(frag)}`.trim()
  return words.every(w => containsAtWordStart(combined, w))
    ? SEARCH_SCORE.NOTES_ONLY
    : SEARCH_SCORE.NONE
}

// Every word in the (normalized) query must match the start of some word in the
// (normalized) haystack. Kept as the single boolean entry point for callers that
// only filter (wear-log pickers, Home); ranking callers use the score directly.
export function fragranceMatchesSearch(frag, query) {
  return fragranceSearchScore(frag, query) > SEARCH_SCORE.NONE
}
